import { twMerge } from 'tailwind-merge';
import { type ClassValue, clsx } from 'clsx';

import { Time, TType } from '@/types/general';
import {
  differenceInCalendarDays,
  subMonths,
  startOfDay,
  intervalToDuration,
  isBefore,
  isToday,
} from 'date-fns';

export function nextTick(func: () => unknown) {
  setTimeout(func, 0);
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function repeat(times: number) {
  return Array.from(Array(times).keys());
}

export function noop() {
  // Do nothing
}

export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export function formatTime(time: Time) {
  const { hour, minute } = time;
  return `${hour}:${minute}:00`;
}

export function isNotEmpty(str: string) {
  return str.trim().length > 0;
}

export function isTodayOrBefore(date: string) {
  return isToday(date) || isBefore(date, new Date());
}

export function getTrialBannerMessage(
  trial_expires_at: string,
  t: TType,
): string {
  const today = startOfDay(new Date());
  const endDate = startOfDay(new Date(trial_expires_at));
  const startDate = startOfDay(subMonths(endDate, 6));

  const diffDaysToEnd = differenceInCalendarDays(endDate, today);
  const diffDaysFromStart = differenceInCalendarDays(today, startDate);

  if (diffDaysFromStart === 0) {
    return t('trial.activated');
  }

  if (diffDaysToEnd === 0) {
    return t('trial.last-day');
  }

  if (diffDaysToEnd < 0) {
    return t('trial.expired');
  }

  const { months = 0, days = 0 } = intervalToDuration({
    start: today,
    end: endDate,
  });

  const parts: string[] = [];
  if (months > 0) parts.push(t('trial.months', { count: months }));
  if (days > 0) parts.push(t('trial.days', { count: days }));

  return t('trial.remaining', { duration: parts.join(` ${t('trial.and')} `) });
}

export function formatOrderCutoffTime(time: string) {
  const [hour, minute] = time.split(':');
  return { hour, minute };
}
